import type { HubConnection} from '@microsoft/signalr'
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr'
import React, { useContext, useEffect, useState } from 'react'
import { usePageFreezeStatus } from '../hooks/usePageFreezeStatus'

export const signalRContext = React.createContext<HubConnection | null>(null)

interface SignalRProviderProps {
  url: string
  children: React.ReactNode
  connect?: boolean
}

export const SignalRProvider: React.FC<SignalRProviderProps> = ({
  url,
  children,
  connect = true,
}) => {
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null)

  usePageFreezeStatus(
    () => connection?.stop(),
    () => connection?.start(),
  )

  useEffect(() => {
    if (!url || !connect) return

    let isUnmounted = false

    // vytvoření connection
    const conn = new HubConnectionBuilder()
      .configureLogging(LogLevel.Warning)
      .withUrl(url)
      .withAutomaticReconnect()
      .build()

    // fce pro navázání spojení
    const startConnection = async () => {
      try {
        console.debug('Starting SignalR connection...')

        // nastartování spojení
        await conn.start()

        console.debug('SignalR connection started.')

        // pokud během navazování spojení došlo k unmount komponenty, tak spojení rovnou ukončíme
        if (isUnmounted) {
          conn.stop()
          setConnection(null)
          console.debug('SignalR connection stopped. Component unmounted while connecting.')
        } else {
          // jinak nastavíme do contextu
          setConnection(conn)
        }
      } catch (error) {
        console.error('Error starting SignalR connection:', error)
      }
    }

    // zastavení connection pokud už. zavírá např. okno
    window.onbeforeunload = function (e) {
      if (conn.state !== HubConnectionState.Disconnected) {
        conn.stop()

        console.debug('SignalR connection stopped before unload.')
      }
    }

    // vyvolání navázání spojení
    if (!isUnmounted) startConnection()

    // Ukončení spojení při cleanup. Může se stát, že ještě není spojení navázáno.
    // Proto si do lokální proměnné jenom uložím info o tom, že došlo k unmountnutí a po navázání se zase zastaví viz výše.
    return () => {
      isUnmounted = true
      if (conn.state !== HubConnectionState.Disconnected) {
        conn.stop()
        setConnection(null)
      }
    }
  }, [url, connect])

  return <signalRContext.Provider value={connection}>{children}</signalRContext.Provider>
}

// Vlastní hook pro snadný přístup k hodnotě kontextu
export const useSignalR = () => useContext(signalRContext)
